/**
 * Yahtzee Game - Main Container Component
 *
 * Orchestrates: mode selection -> engine init -> dice rolling ->
 * hold/release -> category selection -> turn management -> game over.
 *
 * Wrapped in ErrorBoundary. Uses useAuthentication().
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { NotificationType } from '../../services/NotificationService';
import { GameSessionManager } from '../../services/GameSessionManager';
import { YahtzeeEngine } from '../YahtzeeEngine';
import { YahtzeeAI } from '../YahtzeeAI';
import { YahtzeeBoard } from './YahtzeeBoard';
import UniversalGameModeSelector, { GameModeConfig, GameModeSelection } from '../../components/UniversalGameModeSelector';
import { YahtzeeErrorHandler } from '../services/YahtzeeErrorHandler';
import { YahtzeeStatisticsService } from '../services/YahtzeeStatisticsService';
import { YahtzeePersistence } from '../services/YahtzeePersistence';
import { MultiplayerYahtzeeService } from '../services/MultiplayerYahtzeeService';
import { WebSocketService } from '../../services/WebSocketService';
import { ErrorHandlingService } from '../../services/ErrorHandlingService';
import { NotificationService } from '../../services/NotificationService';
import { StatisticsManager } from '../../services/StatisticsManager';
import { ProfileManager } from '../../services/ProfileManager';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import {
  YahtzeeGameSettings,
  YahtzeeGameState,
  YahtzeeGameProps,
  YahtzeeGameResult,
  ScoreCategory,
  Die,
  YahtzeeDifficulty,
} from '../types';
import './Yahtzee.css';

type GameView = 'mode-select' | 'playing' | 'game-over';

const YahtzeeInner: React.FC<YahtzeeGameProps> = ({
  onExit,
  initialRoomCode,
}) => {
  const { isAuthenticated, user } = useAuthentication();
  const { addNotification } = useNotifications();

  // Game state
  const [view, setView] = useState<GameView>('mode-select');
  const [settings, setSettings] = useState<YahtzeeGameSettings | null>(null);
  const [gameState, setGameState] = useState<YahtzeeGameState | null>(null);
  const [isRolling, setIsRolling] = useState(false);

  // Multiplayer state
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [waitingForOpponent, setWaitingForOpponent] = useState(false);

  // Refs
  const engineRef = useRef<YahtzeeEngine>(new YahtzeeEngine());
  const aiRef = useRef<YahtzeeAI | null>(null);
  const gameSessionIdRef = useRef<string | null>(null);
  const gameSessionManager = useRef(GameSessionManager.getInstance());

  // Services (lazy init)
  const errorHandlerRef = useRef<YahtzeeErrorHandler | null>(null);
  const statsServiceRef = useRef<YahtzeeStatisticsService | null>(null);
  const persistenceRef = useRef<YahtzeePersistence | null>(null);

  // Initialize services once
  useEffect(() => {
    try {
      const errorService = ErrorHandlingService.getInstance();
      const notifService = NotificationService.getInstance();
      errorHandlerRef.current = new YahtzeeErrorHandler(errorService, notifService);

      const profileManager = new ProfileManager();
      const statsManager = new StatisticsManager(profileManager);
      statsServiceRef.current = new YahtzeeStatisticsService(statsManager);

      const storageManager = new LocalStorageManager();
      persistenceRef.current = new YahtzeePersistence(storageManager);
    } catch (e) {
      console.error('Failed to initialize Yahtzee services:', e);
    }

    return () => {
      aiRef.current?.destroy();
    };
  }, []);

  // Handle AI turns
  useEffect(() => {
    if (!gameState || !settings) return;
    if (gameState.gameStatus.type !== 'active') return;

    const currentPlayer = gameState.players[gameState.currentPlayerIndex];
    if (currentPlayer.type !== 'ai') return;

    const ai = aiRef.current;
    if (!ai) return;

    const engine = engineRef.current;

    // AI turn logic
    const executeAITurn = () => {
      const state = engine.getState();
      const player = state.players[state.currentPlayerIndex];
      const dice = engine.getDice();
      const rollsRemaining = engine.getRollsRemaining();

      if (rollsRemaining === 3) {
        // First roll is mandatory
        ai.scheduleAction(() => {
          handleRoll();
        });
      } else if (ai.shouldRollAgain(dice, rollsRemaining, player, engine)) {
        // Decide holds then roll
        const holdIndices = ai.decideHolds(dice, player, engine);
        // Set holds
        for (let i = 0; i < dice.length; i++) {
          engine.setHold(i, holdIndices.includes(i));
        }
        ai.scheduleAction(() => {
          handleRoll();
        });
      } else {
        // Score a category
        const category = ai.decideCategory(dice, player, engine);
        ai.scheduleAction(() => {
          handleScoreCategory(category);
        });
      }
    };

    const timeout = setTimeout(executeAITurn, 600);
    return () => clearTimeout(timeout);
  }, [gameState?.currentPlayerIndex, gameState?.rollsRemaining, gameState?.gameStatus]);

  // =========================================================================
  // Game Flow
  // =========================================================================

  const startGame = useCallback((modeSettings: YahtzeeGameSettings) => {
    const engine = engineRef.current;
    engine.initializeGame(modeSettings);

    if (modeSettings.mode === 'singleplayer' && modeSettings.aiDifficulty) {
      aiRef.current = new YahtzeeAI(modeSettings.aiDifficulty);
    }

    setSettings(modeSettings);
    setGameState(engine.getState());
    setView('playing');

    // Track session
    const sessionId = gameSessionManager.current.startGameSession('yahtzee');
    gameSessionIdRef.current = sessionId;

    addNotification({
      type: NotificationType.SUCCESS,
      title: 'Yahtzee',
      message: 'Game started! Roll the dice to begin.',
      duration: 3000,
    });
  }, [addNotification]);

  const handleModeSelect = useCallback((selection: GameModeSelection) => {
    const modeSettings: YahtzeeGameSettings = {
      mode: selection.mode === 'ai' ? 'singleplayer' :
            selection.mode === 'local' ? 'local-multiplayer' : 'multiplayer-realtime',
      aiDifficulty: selection.difficulty as YahtzeeDifficulty,
      playerCount: selection.mode === 'ai' ? 2 : 2,
      aiOpponentCount: selection.mode === 'ai' ? 1 : undefined,
    };

    if (modeSettings.mode === 'multiplayer-realtime') {
      // Online multiplayer - would need room creation
      addNotification({
        type: NotificationType.INFO,
        title: 'Yahtzee',
        message: 'Creating online room...',
        duration: 3000,
      });
      setWaitingForOpponent(true);
      return;
    }

    startGame(modeSettings);
  }, [startGame, addNotification]);

  // =========================================================================
  // Game Actions
  // =========================================================================

  const handleRoll = useCallback(() => {
    const engine = engineRef.current;
    if (engine.getRollsRemaining() <= 0) return;
    if (engine.getState().gameStatus.type !== 'active') return;

    setIsRolling(true);

    // Animate for a short time, then show result
    setTimeout(() => {
      engine.rollDice();
      setGameState(engine.getState());
      setIsRolling(false);

      // Auto-save
      if (persistenceRef.current && settings) {
        persistenceRef.current.autoSave(settings.mode, engine.getState(), {
          aiDifficulty: settings.aiDifficulty,
          startedAt: engine.getState().startedAt,
          lastSavedAt: Date.now(),
        });
      }
    }, 400);
  }, [settings]);

  const handleToggleHold = useCallback((dieIndex: number) => {
    const engine = engineRef.current;
    const state = engine.getState();

    // Only human players can toggle holds
    const currentPlayer = state.players[state.currentPlayerIndex];
    if (currentPlayer.type !== 'human') return;

    engine.toggleHold(dieIndex);
    setGameState(engine.getState());
  }, []);

  const handleScoreCategory = useCallback((category: ScoreCategory) => {
    const engine = engineRef.current;
    const state = engine.getState();

    // Only current player can score
    const currentPlayer = state.players[state.currentPlayerIndex];

    // Must have rolled at least once
    if (state.rollsRemaining === 3) {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Yahtzee',
        message: 'You must roll the dice at least once before scoring.',
        duration: 3000,
      });
      return;
    }

    const result = engine.scoreCategory(category);

    if (!result.isValid) {
      addNotification({
        type: NotificationType.WARNING,
        title: 'Yahtzee',
        message: 'That category has already been scored.',
        duration: 3000,
      });
      return;
    }

    const newState = engine.getState();
    setGameState(newState);

    // Check if game ended
    if (newState.gameStatus.type === 'completed') {
      handleGameOver(newState);
    }

    // Auto-save
    if (persistenceRef.current && settings) {
      persistenceRef.current.autoSave(settings.mode, newState, {
        aiDifficulty: settings.aiDifficulty,
        startedAt: newState.startedAt,
        lastSavedAt: Date.now(),
      });
    }
  }, [settings, addNotification]);

  const handleGameOver = useCallback((finalState: YahtzeeGameState) => {
    setView('game-over');

    const engine = engineRef.current;
    const winnerId = engine.getWinnerId();
    const winner = finalState.players.find((p) => p.id === winnerId);

    // End session
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, true);
      gameSessionIdRef.current = null;
    }

    // Record statistics
    if (statsServiceRef.current && settings) {
      const humanPlayer = finalState.players.find((p) => p.type === 'human');
      const playerScore = humanPlayer ? engine.getTotalScore(humanPlayer) : 0;
      const isWinner = humanPlayer?.id === winnerId;

      const yahtzeesRolled = finalState.players.reduce(
        (total, p) => total + p.scorecard.yahtzeeBonusCount + (p.scorecard.scores.yahtzee && p.scorecard.scores.yahtzee > 0 ? 1 : 0),
        0
      );

      const result: YahtzeeGameResult = {
        gameId: finalState.gameId,
        mode: settings.mode,
        difficulty: settings.aiDifficulty,
        playerCount: finalState.players.length,
        winner: winner?.name,
        scores: Object.fromEntries(
          finalState.players.map((p) => [p.name, engine.getTotalScore(p)])
        ),
        totalRounds: 13,
        yahtzeesRolled,
        duration: Date.now() - finalState.startedAt,
        completedAt: Date.now(),
      };

      statsServiceRef.current.recordGameResult(result, playerScore, isWinner);
    }

    // Clear auto-save
    persistenceRef.current?.clearAutoSave();

    addNotification({
      type: NotificationType.SUCCESS,
      title: 'Game Over!',
      message: winner ? `${winner.name} wins!` : 'Game complete!',
      duration: 5000,
    });
  }, [settings, addNotification]);

  const handleNewGame = useCallback(() => {
    aiRef.current?.destroy();
    aiRef.current = null;
    setView('mode-select');
    setGameState(null);
    setSettings(null);
    setIsRolling(false);
  }, []);

  const handleExit = useCallback(() => {
    // End session if active
    if (gameSessionIdRef.current) {
      gameSessionManager.current.endGameSession(gameSessionIdRef.current, false);
      gameSessionIdRef.current = null;
    }
    aiRef.current?.destroy();
    onExit?.();
  }, [onExit]);

  // =========================================================================
  // Render
  // =========================================================================

  const gameModeConfig: GameModeConfig = {
    gameName: 'Yahtzee',
    gameIcon: '\uD83C\uDFB2',
    availableModes: ['ai', 'local', 'online'],
  };

  if (view === 'mode-select') {
    return (
      <div className="yahtzee">
        <div className="yahtzee__header">
          <button className="yahtzee__back-btn" onClick={handleExit}>
            ← Back
          </button>
          <h1 className="yahtzee__title">🎲 Yahtzee</h1>
        </div>
        <UniversalGameModeSelector
          config={gameModeConfig}
          onStart={handleModeSelect}
          onCreateRoom={() => {
            setWaitingForOpponent(true);
          }}
          onJoinRoom={() => {
            setWaitingForOpponent(true);
          }}
          guestGamesRemaining={3}
          isAuthenticated={isAuthenticated}
          isLoading={false}
        />
      </div>
    );
  }

  if (!gameState) return null;

  const engine = engineRef.current;
  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const isHumanTurn = currentPlayer.type === 'human';

  return (
    <div className="yahtzee">
      <div className="yahtzee__header">
        <button className="yahtzee__back-btn" onClick={handleExit}>
          ← Back
        </button>
        <h1 className="yahtzee__title">🎲 Yahtzee</h1>
        {view === 'game-over' && (
          <button className="yahtzee__new-game-btn" onClick={handleNewGame}>
            New Game
          </button>
        )}
      </div>

      <YahtzeeBoard
        gameState={gameState}
        engine={engine}
        isRolling={isRolling}
        isHumanTurn={isHumanTurn}
        onRoll={handleRoll}
        onToggleHold={handleToggleHold}
        onScoreCategory={handleScoreCategory}
        isGameOver={view === 'game-over'}
      />

      {view === 'game-over' && (
        <div className="yahtzee__game-over">
          <h2 className="yahtzee__game-over-title">Game Over!</h2>
          <div className="yahtzee__final-scores">
            {gameState.players.map((player) => (
              <div
                key={player.id}
                className={`yahtzee__final-score ${
                  player.id === engine.getWinnerId() ? 'yahtzee__final-score--winner' : ''
                }`}
              >
                <span className="yahtzee__final-score-name">{player.name}</span>
                <span className="yahtzee__final-score-value">
                  {engine.getTotalScore(player)} pts
                </span>
                {player.id === engine.getWinnerId() && (
                  <span className="yahtzee__winner-badge">Winner!</span>
                )}
              </div>
            ))}
          </div>
          <button className="yahtzee__play-again-btn" onClick={handleNewGame}>
            Play Again
          </button>
        </div>
      )}
    </div>
  );
};

export const YahtzeeGame: React.FC<YahtzeeGameProps> = (props) => {
  return (
    <ErrorBoundary componentName="YahtzeeGame">
      <YahtzeeInner {...props} />
    </ErrorBoundary>
  );
};
